import { api } from "@/lib/api";
import type { BooksDoc } from "./deals-api";
import { t } from "@/lib/i18n";

/* Этап 6: финансы из Zoho Books (только чтение) — сводка и документы клиента по зеркалу заказов, счетов, оплат и кредит-нот. */

export type FinanceSummary = {
  currency: string;
  revenue: number;
  revenue12m: number;
  /** карточка клиента: выручка за предыдущие 12 месяцев и дата первого счёта */
  revenuePrev12m: number;
  firstInvoiceAt: string | null;
  invoices: number;
  openInvoices: number;
  receivable: number;
  overdue: number;
  overdueDays: number;
  avgCheck: number;
  avgPayDays: number | null;
  paid: number;
  credits: number;
  lastOrderAt: string | null;
  lastInvoiceAt: string | null;
  lastPaymentAt: string | null;
  counts: Record<BooksDoc["kind"], number>;
};

export type CustomerFinance = {
  summary: FinanceSummary;
  docs: BooksDoc[];
  total: number;
  customers: { booksId: string; name: string; booksUrl: string; salesperson: string }[];
  canAll: boolean;
};

export type FinanceParent = { companyId: string } | { contactId: string } | { booksId: string };

/** строка дебиторки — клиент Books с открытыми счетами (раздел «Финансы») */
export type Receivable = {
  booksId: string;
  name: string;
  booksUrl: string;
  salesperson: string;
  companyId: string | null;
  contactId: string | null;
  currency: string;
  openInvoices: number;
  receivable: number;
  overdue: number;
  overdueDays: number;
  oldestDueAt: string | null;
  lastInvoiceAt: string | null;
  lastPaymentAt: string | null;
};

export type Receivables = {
  items: Receivable[];
  totals: { customers: number; overdueCustomers: number; receivable: number; overdue: number; openInvoices: number; today: string };
  aging: { current: number; d1_30: number; d31_60: number; d61_90: number; d90plus: number };
  canAll: boolean;
  sellers: string[];
};

const parentQuery = (p: FinanceParent) => ("companyId" in p ? `company=${p.companyId}` : "contactId" in p ? `contact=${p.contactId}` : `books=${encodeURIComponent(p.booksId)}`);

export const financeApi = {
  customer: (p: FinanceParent) => api<CustomerFinance>(`/finance/customer?${parentQuery(p)}`),
  receivables: (p: { q?: string; mine?: boolean; overdue?: boolean }) =>
    api<Receivables>(`/finance/receivables?q=${encodeURIComponent(p.q ?? "")}${p.mine ? "&mine=1" : ""}${p.overdue ? "&overdue=1" : ""}`),
};

export const DOC_STATUS: Record<string, string> = {
  draft: t("черновик"),
  open: t("открыт"),
  invoiced: t("выставлен счёт"),
  partially_invoiced: t("частично выставлен"),
  overdue: t("просрочен"),
  void: t("аннулирован"),
  sent: t("отправлен"),
  paid: t("оплачен"),
  partially_paid: t("частично оплачен"),
  unpaid: t("не оплачен"),
  closed: t("закрыт"),
  pending_approval: t("на утверждении"),
  approved: t("утверждён"),
};

export function docTitle(o: BooksDoc): string {
  switch (o.kind) {
    case "invoice":
      return t("Счёт {n}", { n: o.number });
    case "salesorder":
      return t("Заказ {n}", { n: o.number });
    case "payment":
      return t("Оплата {n}", { n: o.number });
    default:
      return t("Кредит-нота {n}", { n: o.number });
  }
}
